/**
 * The client entry — mount, providers, error boundary (RO-0-05).
 *
 * Three things happen here and nowhere else:
 *
 *   1. The ROUTER SEAM is fed. `packages/ui/src/router.tsx` (RO-4-01) defaults to
 *      a Next adapter; this host installs the TanStack one, making it the FIRST
 *      non-Next host to supply a `RouterAdapter`. Every `RouterLink` /
 *      `useNavigate` / `usePathname` / `useSearch` in `packages/ui` resolves
 *      through it from here on, so RO-4-02 can migrate call sites without any of
 *      them learning which router they are under.
 *
 *   2. The THEME is applied to <html>. The Next layout did this server-side; the
 *      HTML ships a default and this reconciles it (see `shell/theme.ts`).
 *
 *   3. SHELL DATA is seeded from `GET /api/shell` (RO-11-01). On failure the
 *      declared fallback still paints so the SPA is not blank.
 *
 * NOTE the two `RouterProvider`s. They are different things and both are
 * required: the seam's provider carries the ADAPTER (an object), TanStack's
 * carries the ROUTER (the tree + history). The seam's sits outside because its
 * adapter's hooks are only ever CALLED by components deeper in the tree, which
 * are inside TanStack's provider by then.
 */

import * as React from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider as TanStackRouterProvider } from "@tanstack/react-router";
import { RouterProvider as RouterSeamProvider } from "@excellent/ui/router";
import { Button } from "@excellent/ui/button";
import { apiCall } from "@excellent/ui/api";
import { shell } from "@excellent/ui/api/contracts/shell";
import { tanstackAdapter } from "./shell/router-adapter";
import { resolveTheme, applyTheme } from "./shell/theme";
import { getShellData, setShellData, SHELL_DATA_FALLBACK } from "./shell/shell-data";
import { router } from "./routes/tree";
import "./styles.css";

/**
 * Last line of defence. A thrown render error in an SPA blanks the page —
 * there is no server to fall back to and no error.tsx boundary from the
 * framework — so the shell owns one explicitly.
 *
 * It renders the message, not a generic apology: this app is a local tool whose
 * user is also its operator, and a swallowed stack here costs more than it
 * protects. Nothing sensitive is in a client render error that is not already in
 * the bundle.
 */
class ShellErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[client] unhandled render error:", error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div role="alert" className="p-8 flex flex-col gap-3">
        <h1 className="type-heading font-medium text-foreground">Something broke rendering this page.</h1>
        <pre className="type-subtext text-destructive whitespace-pre-wrap font-mono">
          {this.state.error.message}
        </pre>
        <Button variant="outline" onClick={() => window.location.reload()} className="self-start">
          Reload
        </Button>
      </div>
    );
  }
}

// ── boot ────────────────────────────────────────────────────────────────────

async function boot(): Promise<void> {
  // The server stamps the real PTY port onto <html data-pty-port> (the desktop
  // wrapper picks an ephemeral one when 3001 is taken). Prefer the HTML stamp
  // over the response field so a misconfigured env on the API path cannot
  // desync the terminal client from the PTY that actually bound.
  const htmlPtyPort = Number(document.documentElement.dataset.ptyPort) || 3001;

  // RO-10-04. `style-src-elem` carries no 'unsafe-inline', so the ONE legitimate
  // injector of a <style> element in this bundle — `react-remove-scroll-bar`, the
  // scroll-lock every Radix Dialog / Sheet / Popover mounts — has to be nonced or
  // its style is silently dropped and the page jumps as the scrollbar vanishes.
  // It looks for `__webpack_nonce__` as a bare global (a webpack convention the
  // library kept), which resolves through the scope chain to `window`.
  //
  // This MUST run before the first render below: the library reads the global at
  // injection time, and a modal opened before it is set would inject unnonced.
  // Blank means no policy is being enforced (EXCELLENT_CSP=0) — leave the global
  // unset rather than stamping `nonce=""`, which no source list matches.
  const styleNonce = document.documentElement.dataset.styleNonce;
  if (styleNonce) {
    (window as typeof window & { __webpack_nonce__?: string }).__webpack_nonce__ = styleNonce;
  }

  // Optional project scope from the URL so rail.sections match the active project
  // on first paint (mirrors layout's resolveScopeFromPath).
  const projectMatch = window.location.pathname.match(/^\/projects\/([^/]+)/);
  const projectSlug = projectMatch?.[1];

  try {
    const data = await apiCall(shell.get, {
      query: projectSlug ? { projectSlug } : undefined,
    });
    setShellData({
      ...data,
      lexicon: data.lexicon ?? null,
      ptyPort: htmlPtyPort || data.ptyPort || 3001,
    });
  } catch (err) {
    console.error("[client] GET /api/shell failed; using SHELL_DATA_FALLBACK", err);
    setShellData({ ...SHELL_DATA_FALLBACK, ptyPort: htmlPtyPort });
  }

  const shellData = getShellData();
  applyTheme(
    document.documentElement,
    resolveTheme(shellData.theme.dbTheme, shellData.theme.accent),
  );

  const container = document.getElementById("root");
  if (!container) throw new Error("[client] #root is missing from index.html");

  createRoot(container).render(
    <React.StrictMode>
      <ShellErrorBoundary>
        <RouterSeamProvider adapter={tanstackAdapter}>
          <TanStackRouterProvider router={router} />
        </RouterSeamProvider>
      </ShellErrorBoundary>
    </React.StrictMode>,
  );
}

void boot();
